import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Building2, DoorOpen, Import, KeyRound, LayoutGrid, UserCog, Users } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, formatNumber, hasPermission, labelOf, UNIT_TYPE_LABELS } from '@blimo/shared';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, ErrorState, PageHeader, StatCard, StatusBadge } from '@blimo/ui';

const fmt = (value) => formatNumber(value, { maximumFractionDigits: 0 });

export function DashboardPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { data, error, isPending, refetch } = useQuery(adminQueries.dashboard(api));
  const kpis = data?.data ?? {};

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={
          <>
            {me ? `Willkommen, ${me.name}.` : null}
            {data?.generated_at && <span className="ml-1">Stand: {formatDateTime(data.generated_at)}</span>}
          </>
        }
      />
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : isPending ? (
        <KpiGrid>
          {Array.from({ length: 4 }, (_, i) => (
            <StatCard key={i} label="Wird geladen …" loading />
          ))}
        </KpiGrid>
      ) : Object.keys(kpis).length === 0 ? (
        <EmptyState
          icon={LayoutGrid}
          title="Keine Kennzahlen verfügbar"
          description="Für Ihre Berechtigungen liefert das Dashboard derzeit keine Kennzahlen."
        />
      ) : (
        <DashboardContent kpis={kpis} me={me} />
      )}
    </>
  );
}

function KpiGrid({ children }) {
  return (
    <section aria-labelledby="kpi-heading" className="flex flex-col gap-3">
      <h2 id="kpi-heading" className="sr-only">
        Kennzahlen
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{children}</div>
    </section>
  );
}

function DashboardContent({ kpis, me }) {
  const { properties, units, owners, users } = kpis;
  const hasMigration = 'last_migration_run' in kpis;
  return (
    <>
      <KpiGrid>
        {properties && (
          <StatCard
            label="Liegenschaften"
            icon={Building2}
            value={fmt(properties.total)}
            hint={properties.portal_enabled !== undefined ? `${fmt(properties.portal_enabled)} im Kundenportal freigeschaltet` : undefined}
          />
        )}
        {units && <StatCard label="Einheiten" icon={DoorOpen} value={fmt(units.total)} hint="ohne archivierte Einheiten" />}
        {units && units.rented !== undefined && (
          <StatCard
            label="Vermietet"
            icon={KeyRound}
            value={fmt(units.rented)}
            hint={units.without_current_tenancy !== undefined ? `${fmt(units.without_current_tenancy)} ohne aktuelles Mietverhältnis` : undefined}
          />
        )}
        {owners && <StatCard label="Eigentümer (aktuell)" icon={Users} value={fmt(owners.current)} />}
        {users && (
          <StatCard
            label="Aktive Benutzer"
            icon={UserCog}
            value={fmt(users.active)}
            hint={`${fmt(users.invited)} eingeladen · ${fmt(users.locked)} gesperrt`}
          />
        )}
      </KpiGrid>

      {(units?.by_type || hasMigration) && (
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {units?.by_type && <UnitsByTypeCard byType={units.by_type} total={units.total} />}
          {hasMigration && <MigrationRunCard run={kpis.last_migration_run} canOpen={hasPermission(me, 'import-runs.view')} />}
        </div>
      )}
    </>
  );
}

/** Horizontal bar list. Bar width is only a visual proportion of the API counts. */
function UnitsByTypeCard({ byType, total }) {
  const entries = Object.entries(byType ?? {})
    .map(([key, count]) => ({ key, count: Number(count) || 0 }))
    .sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...entries.map((e) => e.count));
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Einheiten nach Typ</CardTitle>
        <CardDescription>{fmt(total)} Einheiten insgesamt</CardDescription>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <EmptyState compact icon={DoorOpen} title="Noch keine Einheiten" />
        ) : (
          <ul className="flex flex-col gap-2" aria-label="Einheiten nach Typ">
            {entries.map((entry) => {
              const label = !entry.key || entry.key === 'none' || entry.key === 'null' ? 'Ohne Typ' : labelOf(UNIT_TYPE_LABELS, entry.key);
              return (
                <li key={entry.key || 'none'} className="grid grid-cols-[8rem_1fr_3.5rem] items-center gap-2 text-sm">
                  <span className="truncate">{label}</span>
                  <span className="h-2 rounded-full bg-muted" aria-hidden="true">
                    <span className="block h-2 rounded-full bg-chart-1" style={{ width: `${(entry.count / max) * 100}%` }} />
                  </span>
                  <span className="numeric text-right font-medium">{fmt(entry.count)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function MigrationRunCard({ run, canOpen }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Letzter Migrationslauf</CardTitle>
        <CardDescription>Datenübernahme aus den Altsystemen</CardDescription>
      </CardHeader>
      <CardContent>
        {!run ? (
          <EmptyState compact icon={Import} title="Noch kein Migrationslauf" description="Sobald eine Migration gestartet wurde, erscheint ihr Status hier." />
        ) : (
          <div className="flex flex-col gap-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge domain="migrationRun" status={run.status} />
              {run.dry_run && <Badge variant="outline">Probelauf</Badge>}
              <span className="numeric text-muted-foreground">Lauf #{run.id}</span>
            </div>
            <dl className="grid grid-cols-[9rem_1fr] gap-y-1">
              <dt className="text-muted-foreground">Beendet</dt>
              <dd className="numeric">{run.finished_at ? formatDateTime(run.finished_at) : 'noch nicht beendet'}</dd>
              <dt className="text-muted-foreground">Blocker</dt>
              <dd>
                <Badge variant={run.blockers > 0 ? 'destructive' : 'success'}>{fmt(run.blockers)}</Badge>
              </dd>
            </dl>
            {canOpen && (
              <Link to="/administration/importe" className="w-fit text-primary hover:underline">
                Zu den Importen
              </Link>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
